import type { ProTableProps } from '@ant-design/pro-components'
import type { TableProps } from 'antd'
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useTransition
} from 'react'
import type { RefObject } from 'react'

import {
  generateAutoColumnsState,
  generateTablePersistenceKey
} from 'src/utils'

import type { ColumnSettingsPersistence } from './ColumnSettings/types'
import { withDefaultHidden } from './ColumnSettings/utils'
import {
  isSameColumnState,
  proTableColumnsState,
  toProColumnsState
} from './treadmillMath'
import type { ColumnPins, ColumnStateMap } from './treadmillMath'

const WIDTH_CHANGE_THRESHOLD = 1
const COLUMN_WIDTHS_SUFFIX = ':widths'
const TABLE_VIEWPORT_SELECTOR = '.ant-table-container'

export const useContainerWidth = (
  ref: RefObject<HTMLElement | null>
): number => {
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const root = ref.current

    if (!root || typeof ResizeObserver === 'undefined') {
      return
    }

    let viewport: Element = root.querySelector(TABLE_VIEWPORT_SELECTOR) ?? root

    const publish = () => {
      const next = viewport.clientWidth

      setWidth((previous) =>
        Math.abs(previous - next) > WIDTH_CHANGE_THRESHOLD ? next : previous
      )
    }

    const observer = new ResizeObserver(() => {
      if (viewport === root) {
        const rendered = root.querySelector(TABLE_VIEWPORT_SELECTOR)

        if (rendered) {
          viewport = rendered
          observer.unobserve(root)
          observer.observe(rendered)
        }
      }

      publish()
    })

    publish()
    observer.observe(viewport)

    return () => observer.disconnect()
  }, [ref])

  return width
}

const scrollParentOf = (node: HTMLElement) => {
  let parent = node.parentElement

  while (parent) {
    const { overflowY } = getComputedStyle(parent)

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }

    parent = parent.parentElement
  }

  return undefined
}

export const STUCK_HEADER_ATTRIBUTE = 'data-header-stuck'
export const STICKY_OFFSET_VARIABLE = '--table-sticky-offset'
const STICKY_HEADER_SELECTOR = '.ant-table-header'
const STUCK_PROBE_PX = 1

export type StickyHeaderMode = 'sticky' | 'virtual' | 'none'

export const useStickyHeader = (
  ref: RefObject<HTMLElement | null>,
  mode: StickyHeaderMode
): number => {
  const [offset, setOffset] = useState(0)

  useLayoutEffect(() => {
    const root = ref.current
    const scrollParent = root && scrollParentOf(root)
    const holder = root?.querySelector(STICKY_HEADER_SELECTOR)

    if (
      !root ||
      !scrollParent ||
      !holder ||
      mode === 'none' ||
      typeof ResizeObserver === 'undefined'
    ) {
      return
    }

    let frame: number | undefined

    const markStuck = () => {
      frame = undefined
      root.toggleAttribute(
        STUCK_HEADER_ATTRIBUTE,
        holder.getBoundingClientRect().top <
          scrollParent.getBoundingClientRect().top
      )
    }
    const scheduleMarkStuck = () => {
      if (frame === undefined) {
        frame = requestAnimationFrame(markStuck)
      }
    }
    const resizeObserver = new ResizeObserver(() => {
      const nextOffset =
        -(parseFloat(getComputedStyle(scrollParent).paddingTop) || 0) -
        STUCK_PROBE_PX

      root.style.setProperty(STICKY_OFFSET_VARIABLE, `${nextOffset}px`)
      setOffset(nextOffset)
      scheduleMarkStuck()
    })

    resizeObserver.observe(scrollParent)
    scrollParent.addEventListener('scroll', scheduleMarkStuck, {
      passive: true
    })

    return () => {
      resizeObserver.disconnect()
      scrollParent.removeEventListener('scroll', scheduleMarkStuck)

      if (frame !== undefined) {
        cancelAnimationFrame(frame)
      }

      root.removeAttribute(STUCK_HEADER_ATTRIBUTE)
      root.style.removeProperty(STICKY_OFFSET_VARIABLE)
    }
  }, [ref, mode])

  return offset
}

export const useDeferredRowSelection = <T>(
  rowSelection: TableProps<T>['rowSelection']
): TableProps<T>['rowSelection'] =>
  useMemo(() => {
    const onSelectionChange = rowSelection?.onChange

    if (!rowSelection || !onSelectionChange) {
      return rowSelection
    }

    return {
      ...rowSelection,
      onChange: ((...args) =>
        startTransition(() =>
          onSelectionChange(...args)
        )) as typeof onSelectionChange
    }
  }, [rowSelection])

export type ColumnWidthMap = Record<string, number>

const readPersisted = <TValue>(key: string | undefined): TValue | undefined => {
  if (!key || typeof window === 'undefined') {
    return undefined
  }

  try {
    const stored = window.localStorage.getItem(key)

    return stored ? (JSON.parse(stored) as TValue) : undefined
  } catch {
    return undefined
  }
}

const writePersisted = (key: string | undefined, value: unknown) => {
  if (!key || typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    return
  }
}

const forgetPersisted = (key: string | undefined) => {
  if (!key || typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.removeItem(key)
  } catch {
    return
  }
}

export const useColumnWidths = (persistenceKey: string | undefined) => {
  const storageKey =
    persistenceKey && `${persistenceKey}${COLUMN_WIDTHS_SUFFIX}`
  const [widths, setWidths] = useState<ColumnWidthMap>(
    () => readPersisted<ColumnWidthMap>(storageKey) ?? {}
  )

  useEffect(() => {
    if (!Object.keys(widths).length) {
      return
    }

    writePersisted(storageKey, widths)
  }, [storageKey, widths])

  const setColumnWidth = useCallback((columnKey: string, width: number) => {
    setWidths((previous) => ({ ...previous, [columnKey]: width }))
  }, [])

  const resetColumnWidths = useCallback(() => {
    setWidths((previous) => (Object.keys(previous).length ? {} : previous))

    forgetPersisted(storageKey)
  }, [storageKey])

  return { columnWidths: widths, setColumnWidth, resetColumnWidths }
}

const readPersistedColumnState = (
  persistenceKey: string | undefined
): ColumnStateMap | undefined => readPersisted<ColumnStateMap>(persistenceKey)

type ColumnsStateOptions<T extends Record<string, unknown>> = {
  columnsState?: ProTableProps<T, Record<string, unknown>>['columnsState']
  tableIdentifier?: string
  columnCount: number
  scope?: Parameters<typeof generateTablePersistenceKey>[1]
  persistence?: ColumnSettingsPersistence
  defaultHiddenKeys?: string[]
  definedPins: ColumnPins
}

const NO_DEFAULT_HIDDEN_KEYS: string[] = []

const KEY_SEPARATOR = '|'

export const useTableColumnsState = <T extends Record<string, unknown>>({
  columnsState,
  tableIdentifier,
  columnCount,
  scope,
  persistence,
  defaultHiddenKeys = NO_DEFAULT_HIDDEN_KEYS,
  definedPins
}: ColumnsStateOptions<T>) => {
  const autoGeneratedColumnsState = useMemo(
    () =>
      generateAutoColumnsState(
        tableIdentifier,
        columnCount,
        scope,
        columnsState
      ),
    [tableIdentifier, columnCount, scope, columnsState]
  )

  const persistenceKey =
    (columnsState ?? autoGeneratedColumnsState)?.persistenceKey ??
    (tableIdentifier
      ? generateTablePersistenceKey(tableIdentifier, scope)
      : undefined)

  const ownColumnsState = useMemo<
    ProTableProps<T, Record<string, unknown>>['columnsState']
  >(
    () =>
      persistenceKey
        ? { persistenceKey, persistenceType: 'localStorage' }
        : undefined,
    [persistenceKey]
  )

  const source = columnsState ?? autoGeneratedColumnsState ?? ownColumnsState

  const { columnWidths, setColumnWidth, resetColumnWidths } =
    useColumnWidths(persistenceKey)

  const storedColumnsMap = persistence?.columnsMap
  const [chosenColumnsMap, setColumnsMap] = useState<
    ColumnStateMap | undefined
  >(
    () =>
      storedColumnsMap ??
      columnsState?.value ??
      columnsState?.defaultValue ??
      readPersistedColumnState(persistenceKey)
  )
  const [adoptedStoredMap, setAdoptedStoredMap] = useState(storedColumnsMap)
  const [isApplyingColumns, startApplyingColumns] = useTransition()
  const defaultHiddenSignature = defaultHiddenKeys.join(KEY_SEPARATOR)
  const columnsMap = useMemo(
    () =>
      withDefaultHidden(
        chosenColumnsMap,
        defaultHiddenSignature
          ? defaultHiddenSignature.split(KEY_SEPARATOR)
          : []
      ),
    [chosenColumnsMap, defaultHiddenSignature]
  )

  if (storedColumnsMap !== adoptedStoredMap) {
    setAdoptedStoredMap(storedColumnsMap)

    if (storedColumnsMap) {
      setColumnsMap(storedColumnsMap)
    }
  }

  useEffect(() => {
    if (
      persistence ||
      !chosenColumnsMap ||
      !Object.keys(chosenColumnsMap).length
    ) {
      return
    }

    writePersisted(persistenceKey, chosenColumnsMap)
  }, [persistence, persistenceKey, chosenColumnsMap])

  const callerOnChange = columnsState?.onChange

  const changeColumnsMap = useCallback(
    (nextMap: ColumnStateMap) => {
      if (isSameColumnState(columnsMap, nextMap)) {
        return
      }

      startApplyingColumns(() => setColumnsMap(nextMap))
      persistence?.saveColumnsMap(nextMap)
      callerOnChange?.(toProColumnsState(nextMap, definedPins) ?? {})
    },
    [columnsMap, persistence, callerOnChange, definedPins]
  )

  const resolvedColumnsState = useMemo(() => {
    const value = toProColumnsState(columnsMap, definedPins)

    if (persistence) {
      return { value }
    }

    return source ? proTableColumnsState(source, value) : undefined
  }, [persistence, source, columnsMap, definedPins])

  const resetColumnsLayout = useCallback(() => {
    resetColumnWidths()
    forgetPersisted(persistenceKey)
    setColumnsMap({})
    persistence?.resetColumnsMap()
  }, [persistenceKey, resetColumnWidths, persistence])

  const reorderColumns = useCallback(
    (orderedKeys: string[]) => {
      const next: ColumnStateMap = { ...columnsMap }

      orderedKeys.forEach((key, index) => {
        next[key] = {
          ...next[key],
          show: next[key]?.show ?? true,
          order: index
        }
      })

      changeColumnsMap(next)
    },
    [columnsMap, changeColumnsMap]
  )

  return {
    columnsMap,
    isApplyingColumns,
    columnWidths,
    setColumnWidth,
    resolvedColumnsState,
    changeColumnsMap,
    reorderColumns,
    resetColumnsLayout
  }
}
